import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid,
  Chip, Stack, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton, Alert,
  FormControl, Select, MenuItem, InputLabel, Paper, Stepper, Step, StepLabel, Tabs, Tab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AgentTemplates } from './AgentTemplates';
import { createAgent, getAvailableEndpoints } from '../../api/services/agent-studio/AgentManagement';
import { getModels } from '../../api/services/agent-studio/AgentFeatures';
import { useKeycloak } from '@react-keycloak/web';
import {
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  SupportAgent as SupportAgentIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Campaign as CampaignIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Celebration as CelebrationIcon,
  Rocket as RocketIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

export const PrebuiltAgentWizard = ({ onComplete, onCancel }) => {
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    templateId: '',
    tasks: [],
    languages: ['english'],
    language: 'en',
    model: '',
    endpoint: '',
    system_prompt: '',
    models: []
  });
  
  const [templates, setTemplates] = useState([]);
  const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
  const [expandedTaskTemplates, setExpandedTaskTemplates] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTasks, setDialogTasks] = useState([]);
  const [dialogTemplateName, setDialogTemplateName] = useState('');
  const [dialogTemplateDescription, setDialogTemplateDescription] = useState('');
  
  // Endpoints and models
  const [endpoints, setEndpoints] = useState([]);
  const [nodes, setNodes] = useState({});
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Deployment states
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentError, setDeploymentError] = useState(null);
  const [deploymentCompleted, setDeploymentCompleted] = useState(false);

  const [visibleChips, setVisibleChips] = useState({});
  const chipContainerRefs = useRef({});
  const chipRefs = useRef({});

  // Tabs state
  const [activeTab, setActiveTab] = useState(0);

  const iconMap = {
    AccountBalance: AccountBalanceIcon,
    Gavel: GavelIcon,
    People: PeopleIcon,
    AttachMoney: AttachMoneyIcon,
    SupportAgent: SupportAgentIcon,
    Business: BusinessIcon,
    Computer: ComputerIcon,
    Campaign: CampaignIcon
  };

  useEffect(() => {
    const templatesArray = Object.entries(AgentTemplates).map(([key, value]) => ({
      id: key,
      name: key,
      description: value.description,
      task_list: value.task_list,
      prompt_template: value.prompt_template,
      icon: value.icon,
      languages: value.languages || ['english']
    }));
    setTemplates(templatesArray);
    if (templatesArray.length > 0) {
      handleTemplateSelect(templatesArray[0].id, templatesArray);
    }
    
    // Fetch available endpoints
    fetchEndpoints();
  }, []);

  const fetchEndpoints = async () => {
    try {
      setLoadingEndpoints(true);
      await keycloak.updateToken(30);
      const response = await getAvailableEndpoints(keycloak.token);
      if (response.success && response.data) {
        const allEndpoints = response.data.endpoints || [];
        const allNodes = response.data.nodes || {};
        
        // Filter endpoints to only include those with healthy nodes
        const healthyEndpoints = allEndpoints.filter(endpoint => {
          const node = allNodes[endpoint];
          return node && (node.healthy === true || node.healthy === undefined); // Include undefined for backward compatibility
        });
        
        setEndpoints(healthyEndpoints);
        setNodes(allNodes);
        
        // Set default endpoint if available
        if (healthyEndpoints.length > 0) {
          setAgentConfig(prev => ({
            ...prev,
            endpoint: healthyEndpoints[0]
          }));
          
          // Fetch models for default endpoint
          fetchModelsForEndpoint(healthyEndpoints[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching endpoints:", error);
    } finally {
      setLoadingEndpoints(false);
    }
  };

  const fetchModelsForEndpoint = async (endpoint) => {
    if (!endpoint) return;
    
    try {
      setLoadingModels(true);
      await keycloak.updateToken(30);
      // Use a dummy API key for now, as we're authenticated via token
      const response = await getModels("dummy-key", endpoint);
      if (response.data) {
        const modelList = response.data.map(model => ({
          id: model.key,
          name: model.key.split('/').pop(),
          value: model.key,
          provider: model.key.split('/')[0]
        }));
        setAvailableModels(modelList);
        
        // Set default model if available
        if (modelList.length > 0) {
          setAgentConfig(prev => ({
            ...prev,
            model: modelList[0].value,
            models: [modelList[0].value]
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching models for endpoint:", error);
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    const newVisibleChips = {};
    templates.forEach((template) => {
      const container = chipContainerRefs.current[template.id];
      if (!container) return;
      let count = 0;
      let firstTop = null;
      let secondTop = null;
      let visibleLines = 0;
      
      for (let i = 0; i < template.task_list.length; i++) {
        const chip = chipRefs.current[`${template.id}-${i}`];
        if (!chip) continue;
        chip.style.display = 'inline-block';
        const top = chip.offsetTop;
        
        if (firstTop === null) {
          firstTop = top;
          visibleLines = 1;
        } else if (top > firstTop && secondTop === null) {
          secondTop = top;
          visibleLines = 2;
        } else if (top > secondTop) {
          break;
        }
        
        count++;
      }
      newVisibleChips[template.id] = count;
    });
    setVisibleChips(newVisibleChips);
  }, [templates]);

  const handleTemplateSelect = (templateId, templatesArr = templates) => {
    if (templateId === 'custom') {
      setAgentConfig(prev => ({
        ...prev,
        name: '',
        templateId: 'custom',
        tasks: [],
        system_prompt: ''
      }));
      return;
    }
    const template = (templatesArr.length ? templatesArr : templates).find(t => t.id === templateId);
    if (template) {
      setAgentConfig(prev => ({
        ...prev,
        name: template.name,
        system_prompt: template.prompt_template,
        templateId: templateId,
        tasks: template.task_list,
        languages: template.languages || ['english'],
        language: template.languages?.includes('english') ? 'en' : 'de'
      }));
    }
  };

  const handleEndpointChange = (endpoint) => {
    setAgentConfig(prev => ({
      ...prev,
      endpoint,
      model: '', // Reset model when endpoint changes
      models: []
    }));
    
    fetchModelsForEndpoint(endpoint);
  };

  const handleModelSelect = (modelId) => {
    setAgentConfig(prev => ({
      ...prev,
      model: modelId
    }));
  };

  const handleModelToggle = (modelId) => {
    setAgentConfig(prev => {
      // Check if this model is already selected
      if (prev.models.includes(modelId)) {
        // If it's also the default model, clear the default
        const newState = {
          ...prev,
          models: prev.models.filter(id => id !== modelId)
        };
        
        if (prev.model === modelId) {
          newState.model = newState.models.length > 0 ? newState.models[0] : '';
        }
        
        return newState;
      } else {
        // Add to selected models and set as default if no default exists
        const newModels = [...prev.models, modelId];
        return {
          ...prev,
          models: newModels,
          model: prev.model || modelId
        };
      }
    });
  };

  const handleLanguageChange = (event) => {
    setAgentConfig(prev => ({
      ...prev,
      language: event.target.value
    }));
  };

  const toggleTasksExpand = (templateId, e) => {
    if (e) e.stopPropagation();
    setExpandedTaskTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const handleReadMore = (tasks, templateName, description, e) => {
    if (e) e.stopPropagation();
    setDialogTasks(tasks);
    setDialogTemplateName(templateName);
    setDialogTemplateDescription(description);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogTasks([]);
    setDialogTemplateName('');
    setDialogTemplateDescription('');
  };

  const handleDeployAgent = async () => {
    setIsDeploying(true);
    setDeploymentError(null);
    
    try {
      // Update the Keycloak token
      await keycloak.updateToken(30);
      const token = keycloak.token;
      
      if (!token) {
        throw new Error("Authentication token is required");
      }
      
      // Prepare agent data based on the configuration
      const agentData = {
        name: agentConfig.name,
        system_prompt: agentConfig.system_prompt || '',
        endpoint: agentConfig.endpoint || '',
        language: agentConfig.language || 'en',
        model: agentConfig.model || '',
        models: agentConfig.models || []
      };
      
      // Call the create agent API with the full agent data
      const response = await createAgent(token, agentData);
      
      if (response.success) {
        // Add the API response data to the agent config
        setAgentConfig(prev => ({
          ...prev,
          apiResponse: response.data
        }));
        setDeploymentCompleted(true);
      } else {
        setDeploymentError(response.error || "Failed to create agent");
      }
    } catch (error) {
      console.error("Agent deployment error:", error);
      setDeploymentError(error.message || "An unexpected error occurred");
    } finally {
      setIsDeploying(false);
    }
  };

  const handleFinish = () => {
    onComplete(agentConfig);
  };

  if (isDeploying) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 1 }}>
          Deploying your AI Agent...
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This may take a few moments while we configure your agent
        </Typography>
        {deploymentError && (
          <Alert severity="error" sx={{ mt: 2, mx: 'auto', maxWidth: 600 }}>
            {deploymentError}
          </Alert>
        )}
      </Box>
    );
  }

  if (deploymentCompleted) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <CelebrationIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
        <Typography variant="h4" gutterBottom>Deployment Successful!</Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Your AI agent "{agentConfig.name}" has been successfully deployed and is now online.
        </Typography>
        <Card sx={{ mb: 4, p: 3, maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom>Deployment Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent ID:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.id || '-'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent Name:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.name || '-'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">API Key:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.apiKey || '-'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Endpoint:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.endpoint || '-'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Models:</Typography></Grid>
            <Grid item xs={8}>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {agentConfig.models.map((model, index) => (
                  <Chip 
                    key={index} 
                    label={model.split('/').pop()} 
                    size="small" 
                    color={model === agentConfig.model ? "primary" : "default"}
                  />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Language:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.language === 'en' ? 'English' : 'German'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Active:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.isActive ? 'Yes' : 'No'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Created At:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.createdAt ? new Date(agentConfig.apiResponse.agent.createdAt).toLocaleString() : '-'}</Typography></Grid>
            <Grid item xs={4}><Typography variant="body2" color="text.secondary">Updated At:</Typography></Grid>
            <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.updatedAt ? new Date(agentConfig.apiResponse.agent.updatedAt).toLocaleString() : '-'}</Typography></Grid>
          </Grid>
        </Card>
        <Button
          variant="contained"
          size="large"
          onClick={handleFinish}
          sx={{ px: 4 }}
        >
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>Create a New AI Agent</Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Agent Name"
          value={agentConfig.name}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, name: e.target.value }))}
          required
          error={!agentConfig.name}
          helperText={!agentConfig.name ? "Please enter a name for your agent" : ""}
          placeholder="Enter a descriptive name for your agent"
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={agentConfig.language}
              label="Language"
              onChange={handleLanguageChange}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="de">German</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <Box sx={{ my: 3 }}>
        <Typography variant="h6" gutterBottom>Choose a Template</Typography>
      <Grid container spacing={2}>
{templates.map((template) => {
    const IconComponent = iconMap[template.icon];
    const totalTasks = template.task_list.length;
            const visibleCount = totalTasks > 4 ? 4 : totalTasks;
    const hasMoreTasks = totalTasks > visibleCount;
    return (
      <Grid item xs={12} sm={6} md={4} key={template.id}>
        <Card
          sx={{
            cursor: 'pointer',
            border: agentConfig.templateId === template.id ? '2px solid' : '1px solid',
                    borderColor: agentConfig.templateId === template.id ? theme.palette.primary.main : 'divider',
            transition: 'all 0.2s',
            minHeight: 210,
            boxShadow: agentConfig.templateId === template.id ? 4 : 1,
            position: 'relative',
            background: hoveredTemplateId === template.id ? 'rgba(0,0,0,0.03)' : 'background.paper',
            zIndex: hoveredTemplateId === template.id ? 2 : 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            p: 2
          }}
          onMouseEnter={() => setHoveredTemplateId(template.id)}
          onMouseLeave={() => setHoveredTemplateId(null)}
          onClick={() => handleTemplateSelect(template.id)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {IconComponent && <IconComponent sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 32 }} />}
            <Typography variant="subtitle1" fontWeight={600}>{template.name}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{template.description}</Typography>
          <Box sx={{ width: '100%', mt: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2
              }}
            >
              {template.task_list.slice(0, visibleCount).map((task, idx) => (
                <Chip
                  key={idx}
                  label={task}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    borderRadius: '16px',
                    fontSize: '12px',
                    height: '24px',
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              ))}
            </Box>
            {hasMoreTasks && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button 
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={e => handleReadMore(template.task_list, template.name, template.description, e)}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2, fontSize: 13, fontWeight: 500 }}
                >
                  Read more
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  +{totalTasks - visibleCount} more
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Grid>
    );
  })}
      </Grid>
      </Box>
      
      <Box sx={{ my: 3 }}>
        <Typography variant="h6" gutterBottom>Server Selection</Typography>
        <Grid container spacing={2}>
          {endpoints.map((endpoint) => {
            const node = nodes[endpoint];
            const isHealthy = node && (node.healthy === true || node.healthy === undefined); // Backward compatibility
            
            return (
              <Grid item xs={12} sm={6} md={4} key={endpoint}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: agentConfig.endpoint === endpoint ? '2px solid' : '1px solid',
                    borderColor: agentConfig.endpoint === endpoint ? theme.palette.primary.main : 'divider',
                    transition: 'all 0.2s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: agentConfig.endpoint === endpoint ? 4 : 1,
                    opacity: isHealthy ? 1 : 0.6,
                  }}
                  onClick={() => isHealthy && handleEndpointChange(endpoint)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 500 }}>{endpoint}</Typography>
                      {node && (
                        <Chip 
                          label={node.healthy === false ? "Unhealthy" : "Healthy"} 
                          size="small" 
                          color={node.healthy === false ? "error" : "success"}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {node && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Location: {node.location}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          GPU: {node.gpu} x {node.noOfGpu}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          System: {node.system}
                        </Typography>
                      </>
                    )}
                    {agentConfig.endpoint === endpoint && (
                      <Chip 
                        label="Selected" 
                        size="small" 
                        color="primary" 
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {loadingEndpoints && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={30} />
            </Grid>
          )}
          {!loadingEndpoints && endpoints.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                No healthy endpoints available. Please try again later or contact support.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={{ my: 3 }}>
        <Typography variant="h6" gutterBottom>Model Selection</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select models for your agent. You can choose multiple models, with one set as the default.
        </Typography>
        <Grid container spacing={2}>
          {availableModels.map((model) => (
            <Grid item xs={12} sm={6} md={4} key={model.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: agentConfig.models.includes(model.id) ? '2px solid' : '1px solid',
                  borderColor: agentConfig.models.includes(model.id) ? theme.palette.primary.main : 'divider',
                  transition: 'all 0.2s',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: agentConfig.models.includes(model.id) ? 4 : 1,
                }}
                onClick={() => handleModelToggle(model.id)}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 500 }}>{model.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {model.provider}
                  </Typography>
                  {agentConfig.models.includes(model.id) && (
                    <Chip 
                      label={agentConfig.model === model.id ? "Default Model" : "Selected"} 
                      size="small" 
                      color={agentConfig.model === model.id ? "primary" : "default"} 
                      sx={{ mb: 1 }}
                    />
                  )}
                </CardContent>
                {agentConfig.models.includes(model.id) && (
                  <Box sx={{ p: 1 }}>
                    <Button
                      variant={agentConfig.model === model.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModelSelect(model.id);
                      }}
                      fullWidth
                      sx={{
                        backgroundColor: agentConfig.model === model.id ? theme.palette.primary.main : 'transparent',
                        color: agentConfig.model === model.id ? 'black' : theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: agentConfig.model === model.id ? theme.palette.primary.dark : 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      {agentConfig.model === model.id ? 'Default Model' : 'Set as Default'}
                    </Button>
                  </Box>
                )}
              </Card>
            </Grid>
          ))}
          {loadingModels && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={30} />
            </Grid>
          )}
          {!loadingModels && availableModels.length === 0 && agentConfig.endpoint && (
            <Grid item xs={12}>
              <Alert severity="info">
                No models available for this endpoint. Please select a different endpoint.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>{dialogTemplateName}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent Overview
            </Typography>
            <Typography variant="body1">
              {dialogTemplateDescription}
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Agent Tasks
              </Typography>
              <Stack direction="column" spacing={1} sx={{ mb: 3 }}>
                {dialogTasks.map((task, idx) => (
                  <Box key={idx} sx={{
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    px: 2,
                    py: 0.5,
                    fontSize: 13,
                    color: 'primary.main',
                    background: 'background.paper',
                    display: 'inline-block',
                    minWidth: 0
                  }}>{task}</Box>
                ))}
              </Stack>
              
              <Typography variant="h6" gutterBottom>
                Supported Languages
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                <Chip label="English" color="primary" variant="outlined" size="small" />
                <Chip label="German" color="primary" variant="outlined" size="small" />
                <Chip label="French" color="primary" variant="outlined" size="small" />
                <Chip label="Spanish" color="primary" variant="outlined" size="small" />
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Available Models
              </Typography>
              <Stack spacing={2} sx={{ mb: 3 }}>
                {availableModels.length > 0 ? (
                  availableModels.slice(0, 3).map((model) => (
                    <Card key={model.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">{model.value}</Typography>
                  <Typography variant="body2" color="text.secondary">
                        {model.provider}
                  </Typography>
                </Card>
                  ))
                ) : (
                  <Typography color="text.secondary">Loading models...</Typography>
                )}
              </Stack>
              
              <Typography variant="h6" gutterBottom>
                Hosting Information
              </Typography>
              <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                  <Typography variant="body1" fontWeight={500}>PioSphere Infrastructure</Typography>
                </Box>
                <Typography variant="body2">
                  {agentConfig.endpoint && nodes[agentConfig.endpoint] ? (
                    `This agent is hosted on ${nodes[agentConfig.endpoint].system} located in ${nodes[agentConfig.endpoint].location}, equipped with ${nodes[agentConfig.endpoint].noOfGpu}x ${nodes[agentConfig.endpoint].gpu} GPUs.`
                  ) : (
                    "This agent is hosted on secure PioSphere infrastructure, compliant with data protection regulations."
                  )}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDeployAgent}
          disabled={!agentConfig.name || !agentConfig.endpoint || !agentConfig.model || agentConfig.models.length === 0}
          endIcon={<RocketIcon />}
          sx={{ minWidth: 120 }}
        >
          Deploy Agent
        </Button>
      </Box>
    </Box>
  );
};

export default PrebuiltAgentWizard;